import { Box, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import useAuth from "hooks/useAuth";
import LoadingSpinner from "components/LoadingSpinner";
import { verifyCognitoAuth } from "app/services/cognito/cognito.service";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { cleanUrl, extractParamsFromUrl } from "utils/cognito";

const CognitoAuthentication = () => {
  const navigate = useNavigate();
  const { handleLogin } = useAuth({});

  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    if (hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;

    const handleCognitoAuthentication = async () => {
      try {
        const authParams = extractParamsFromUrl();

        const { error, code, idToken } = authParams || {};

        if (error || !(code || idToken)) {
          cleanUrl();
          const errorMessage = error || "Authentication failed";
          showErrorToast(errorMessage);
          navigate(`/login?error=${encodeURIComponent(errorMessage)}`);
          return;
        }

        const response = await verifyCognitoAuth({ code, idToken });

        cleanUrl();

        if (!response?.data) {
          throw new Error("Invalid response from Cognito verification");
        }

        if (response?.data?.redirectUri) {
          window.location.href = response.data.redirectUri;
          return;
        }

        await handleLogin(response.data);
        showSuccessToast("Authentication successful");
      } catch (error) {
        cleanUrl();
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Authentication failed";

        showErrorToast(errorMessage);
        navigate(`/login?error=${encodeURIComponent(errorMessage)}`);
      }
    };

    handleCognitoAuthentication();
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={2}
    >
      <LoadingSpinner />
      <Typography variant="h6">Authenticating...</Typography>
    </Box>
  );
};

export default CognitoAuthentication;
